// ============================================================
// Krypto-Service — AES-256-GCM + PBKDF2 (600k Iterationen)
// Spezifikation: Prompt v6.7 Abschnitt C
// ============================================================

import { SessionKeyStore } from './keyStore';

const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES        = 32;  // 256 Bit
const IV_BYTES          = 12;  // 96 Bit für AES-GCM (NIST-Empfehlung)

// ── IndexedDB-Zugriff für Salt (via db-Modul, hier direkt für Bootstrap) ──
const SALT_STORE_NAME  = 'cryptoMeta';
const SALT_KEY         = 'user-salt';

// ── Salt-Verwaltung ────────────────────────────────────────────────────────

/**
 * Liest Salt aus IndexedDB. Gibt null zurück wenn noch keiner existiert
 * (= erster App-Start, Setup nötig).
 */
export async function loadSalt(): Promise<Uint8Array | null> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('AmedDB');
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SALT_STORE_NAME)) {
        db.close();
        resolve(null);
        return;
      }
      const tx    = db.transaction(SALT_STORE_NAME, 'readonly');
      const store = tx.objectStore(SALT_STORE_NAME);
      const get   = store.get(SALT_KEY);
      get.onsuccess = () => {
        db.close();
        const record = get.result as { id: string; value: Uint8Array } | undefined;
        resolve(record?.value ?? null);
      };
      get.onerror = () => { db.close(); reject(get.error); };
    };
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(SALT_STORE_NAME)) {
        db.createObjectStore(SALT_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Speichert ein GEGEBENES Salt (Multi-Device: Salt vom Server übernehmen).
 * Salt ist kryptografisch öffentlich — die Sicherheit liegt im Passwort.
 */
export async function storeSalt(salt: Uint8Array): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.open('AmedDB');
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(SALT_STORE_NAME)) {
        db.createObjectStore(SALT_STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => {
      const db    = req.result;
      const tx    = db.transaction(SALT_STORE_NAME, 'readwrite');
      const store = tx.objectStore(SALT_STORE_NAME);
      const put   = store.put({ id: SALT_KEY, value: salt });
      put.onsuccess = () => { db.close(); resolve(); };
      put.onerror   = () => { db.close(); reject(put.error); };
    };
  });
}

/**
 * Erzeugt neuen zufälligen Salt und speichert ihn in IndexedDB.
 * Darf NUR beim allerersten Setup aufgerufen werden!
 */
export async function createAndStoreSalt(): Promise<Uint8Array> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  await storeSalt(salt);
  return salt;
}

// ── Schlüssel-Ableitung ────────────────────────────────────────────────────

/**
 * Leitet AES-256-GCM-Schlüssel aus Master-Passwort + Salt ab.
 * extractable: false → Schlüssel kann NIEMALS aus dem Browser exportiert werden.
 */
export async function deriveKey(
  masterPassword: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const rawPassword = new TextEncoder().encode(masterPassword);

  const passwordKey = await crypto.subtle.importKey(
    'raw',
    rawPassword,
    'PBKDF2',
    false, // nicht exportierbar
    ['deriveKey'],
  );

  const derivedKey = await crypto.subtle.deriveKey(
    {
      name:       'PBKDF2',
      salt: new Uint8Array(salt.buffer as ArrayBuffer, salt.byteOffset, salt.byteLength),
      iterations: PBKDF2_ITERATIONS,
      hash:       'SHA-256',
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false, // ⚠️ KRITISCH: extractable: false
    ['encrypt', 'decrypt'],
  );

  return derivedKey;
}

/**
 * Login-Flow: Passwort → Key ableiten → in SessionKeyStore speichern.
 * Passwort-String wird nach der Ableitung sofort aus dem Scope entlassen (GC).
 */
export async function unlockSession(masterPassword: string): Promise<void> {
  const salt = await loadSalt();
  if (salt === null) {
    throw new Error('Kein Salt gefunden — Setup wurde noch nicht durchgeführt.');
  }
  const key = await deriveKey(masterPassword, salt);
  SessionKeyStore.set(key);
}

// ── Verschlüsselung / Entschlüsselung ─────────────────────────────────────

/**
 * Verschlüsselt beliebige Bytes.
 * Gibt [IV (12 Byte) || Ciphertext] als Uint8Array zurück.
 * IV wird pro Operation NEU erzeugt — niemals wiederverwenden!
 */
export async function encrypt(
  plaintext: Uint8Array,
  key?: CryptoKey,
): Promise<Uint8Array> {
  const cryptoKey = key ?? SessionKeyStore.get();
  if (!cryptoKey) throw new Error('Kein aktiver Session-Key — bitte einloggen.');

  const iv         = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ivBuf      = iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer;
  const ptBuf      = plaintext.buffer.slice(plaintext.byteOffset, plaintext.byteOffset + plaintext.byteLength) as ArrayBuffer;
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: ivBuf },
    cryptoKey,
    ptBuf,
  );

  // Blob = IV || Ciphertext
  const blob = new Uint8Array(IV_BYTES + ciphertext.byteLength);
  blob.set(new Uint8Array(ivBuf), 0);
  blob.set(new Uint8Array(ciphertext), IV_BYTES);
  return blob;
}

/**
 * Entschlüsselt einen [IV || Ciphertext]-Blob.
 */
export async function decrypt(
  blob: Uint8Array,
  key?: CryptoKey,
): Promise<Uint8Array> {
  const cryptoKey = key ?? SessionKeyStore.get();
  if (!cryptoKey) throw new Error('Kein aktiver Session-Key — bitte einloggen.');
  if (blob.length <= IV_BYTES) throw new Error('Ungültiger Ciphertext-Blob (zu kurz).');

  const iv         = blob.slice(0, IV_BYTES);
  const ciphertext = blob.slice(IV_BYTES);
  const ivBuf      = iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer;
  const ctBuf      = ciphertext.buffer.slice(ciphertext.byteOffset, ciphertext.byteOffset + ciphertext.byteLength) as ArrayBuffer;

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBuf },
    cryptoKey,
    ctBuf,
  );
  return new Uint8Array(plaintext);
}

// ── String-Convenience-Wrapper ─────────────────────────────────────────────

export async function encryptString(text: string, key?: CryptoKey): Promise<Uint8Array> {
  return encrypt(new TextEncoder().encode(text), key);
}

export async function decryptToString(blob: Uint8Array, key?: CryptoKey): Promise<string> {
  const plain = await decrypt(blob, key);
  return new TextDecoder().decode(plain);
}

// ── Hilfsfunktionen für Speicherung ───────────────────────────────────────

/** Uint8Array → Base64-String (für IndexedDB-Serialisierung) */
export function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/** Base64-String → Uint8Array */
export function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
